"use client";

import { Fragment } from "react";
import { Alert, Text } from "@mantine/core";
import { IconAlertCircle, IconHourglass } from "@tabler/icons-react";
import { useStripeTerminalContext } from "@/app/_providers/StripeTerminalProvider";

// A message with any of the shop's reader names in bold, so staff see at a
// glance which reader it is about ("This device could not find **Front
// Counter**."). Names come from the shop's reader list, longest first so a
// name that contains another is matched whole.
export const ReaderNamesBold = ({ text }: { text: string }) => {
  const { readers } = useStripeTerminalContext();
  const names = Array.from(
    new Set(readers.map((reader) => reader.label).filter(Boolean))
  ).sort((a, b) => b.length - a.length);
  if (names.length === 0) return <>{text}</>;

  const escaped = names.map((name) =>
    name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const parts = text.split(new RegExp(`(${escaped.join("|")})`, "g"));
  return (
    <>
      {parts.map((part, index) =>
        names.includes(part) ? (
          <Text key={index} span inherit fw={700}>
            {part}
          </Text>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        )
      )}
    </>
  );
};

// The one way a payment screen says something went wrong, so every payment
// modal looks the same: red for an error (declined card, reader could not be
// reached, payment failed), orange for "wait" (reader busy on another
// device). Title says what failed, message says why and what to do.
interface PaymentNoticeProps {
  tone?: "error" | "wait";
  title: string;
  message: string;
  onClose?: () => void;
}

const PaymentNotice = ({
  tone = "error",
  title,
  message,
  onClose,
}: PaymentNoticeProps) => (
  <Alert
    color={tone === "wait" ? "orange" : "red"}
    variant="light"
    icon={
      tone === "wait" ? (
        <IconHourglass size={18} />
      ) : (
        <IconAlertCircle size={18} />
      )
    }
    title={title}
    withCloseButton={!!onClose}
    onClose={onClose}
  >
    <Text size="sm">
      <ReaderNamesBold text={message} />
    </Text>
  </Alert>
);

export default PaymentNotice;
